var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useConfigContext } from '@/hoc';
import { CloseIcon } from '@/icons';
import { MicIcon } from '@/icons/mic-icon';
import { MicOffIcon } from '@/icons/mic-off-icon';
import { cn, getToken } from '@/utils';
import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { Button } from './ui';
const Modal = ({ onClose }) => {
    const [isMicOff, setIsMicOff] = useState(true);
    const [status, setStatus] = useState('Disconnected');
    const [sessionId, setSessionId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const { config: { voiceType }, } = useConfigContext();
    const { config: { apiBaseUrl, campaignId, chatbotBaseUrl, tokenKey, sessionIdKey, usePayloadKey }, } = useConfigContext();
    const socketRef = useRef(null);
    const streamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const isRecordingRef = useRef(false);
    const sequenceRef = useRef(0);
    const sourceRef = useRef(null);
    // let audioChunks = [];
    let audioChunks = [];
    const playbackContext = new window.AudioContext({
        sampleRate: 16000,
    });
    let nextPlayTime = playbackContext.currentTime;
    useEffect(() => {
        const source = sourceRef.current;
        if (!source)
            return;
        const handleEnded = () => {
            setIsAudioPlaying(false);
        };
        source.onended = handleEnded;
        return () => {
            // Cleanup: remove the event handler
            source.onended = null;
        };
    }, [sourceRef.current]);
    // Requesting mic permission
    const requestMicPermission = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield navigator.mediaDevices.getUserMedia({ audio: true });
            return true;
        }
        catch (error) {
            alert('Microphone permission is required to use voice input.');
            return false;
        }
    });
    const connectToServer = (startRecordingCallback) => {
        if (!apiBaseUrl)
            return alert('Enter server URL');
        socketRef.current = io(apiBaseUrl);
        socketRef.current.on('connect', () => {
            var _a;
            setStatus(`Connected (${(_a = socketRef.current) === null || _a === void 0 ? void 0 : _a.id})`);
            console.log('Connected to server.');
            startRecordingCallback === null || startRecordingCallback === void 0 ? void 0 : startRecordingCallback();
        });
        socketRef.current.on('disconnect', () => {
            setStatus('Disconnected');
            console.log('Disconnected.');
        });
        socketRef.current.on('audio_stream_started', (data) => {
            console.log('Stream started: ' + data.session_id);
            setSessionId(data.session_id);
        });
        socketRef.current.on('audio_chunk_received', (data) => {
            console.log(`Chunk ack: ${data.sequence}, total: ${data.chunks_received}`);
        });
        socketRef.current.on('audio_stream_stopped', (data) => {
            console.log('Stream stopped: ' + JSON.stringify(data));
        });
        socketRef.current.on('audio_error', (data) => {
            console.log('Error: ' + data.message);
        });
        socketRef.current.on('chatbot_audio_chunk', (chunkData) => {
            console.timeEnd('started processing audio');
            console.log('Chatbot audio chunk received');
            setIsLoading(false); // Stop loader
            handleChatbotAudioChunk(chunkData);
        });
        socketRef.current.on('chatbot_audio_chunk_end', () => {
            console.log('Chatbot audio chunk end');
            if (audioChunks.length > 0) {
                const fullAudio = new Uint8Array(audioChunks.reduce((acc, chunk) => acc.concat(Array.from(chunk)), []));
                const wavWithHeader = addWavHeader(fullAudio);
                const blob = new Blob([wavWithHeader], { type: 'audio/wav' });
                const url = URL.createObjectURL(blob);
                setAudioUrl(url);
                audioChunks = []; // Clear for next message
            }
        });
    };
    const addWavHeader = (pcmData, sampleRate = 16000, channels = 1, bitsPerSample = 16) => {
        const byteRate = (sampleRate * channels * bitsPerSample) / 8;
        const blockAlign = (channels * bitsPerSample) / 8;
        const buffer = new ArrayBuffer(44 + pcmData.length);
        const view = new DataView(buffer);
        const writeString = (offset, str) => [...str].forEach((s, i) => view.setUint8(offset + i, s.charCodeAt(0)));
        writeString(0, 'RIFF');
        view.setUint32(4, 36 + pcmData.length, true);
        writeString(8, 'WAVE');
        writeString(12, 'fmt ');
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, channels, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, byteRate, true);
        view.setUint16(32, blockAlign, true);
        view.setUint16(34, bitsPerSample, true);
        writeString(36, 'data');
        view.setUint32(40, pcmData.length, true);
        new Uint8Array(buffer, 44).set(pcmData);
        return buffer;
    };
    const handleChatbotAudioChunk = (chunkData) => {
        const chunk = new Uint8Array(chunkData.audio_chunk);
        const wavBuffer = addWavHeader(chunk);
        audioChunks.push(chunk); // Store for later use
        playbackContext.decodeAudioData(wavBuffer, (decoded) => {
            const source = playbackContext.createBufferSource();
            sourceRef.current = source;
            sourceRef.current.buffer = decoded;
            sourceRef.current.connect(playbackContext.destination);
            setIsAudioPlaying(true);
            const playTime = Math.max(nextPlayTime, playbackContext.currentTime + 0.05);
            sourceRef.current.start(playTime);
            nextPlayTime = playTime + decoded.duration;
        }, (err) => console.log(err));
    };
    const startRecording = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        setIsAudioPlaying(false);
        setAudioUrl(null);
        if (!socketRef.current)
            return;
        setAudioUrl(null);
        try {
            const stream = yield navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            mediaRecorderRef.current = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus',
            });
            const newSessionId = (_a = (sessionIdKey ? getToken(sessionIdKey) : null)) !== null && _a !== void 0 ? _a : 'session_' + Date.now();
            setSessionId(newSessionId);
            sequenceRef.current = 0;
            const payload = {
                session_id: newSessionId,
                campaign_id: campaignId,
                token: (_b = getToken(tokenKey)) !== null && _b !== void 0 ? _b : 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6NjI5MiwibmFtZSI6IkdhanJhaiBUaGFrdXIiLCJpYXQiOjE3NDM2MDU0NjksImV4cCI6MzMzMDEyMDU0Njl9.OH1lkUp-0hONq6UcMwpS-EWVCw7R3YOVkSjSbaC70_0',
                env_url: chatbotBaseUrl,
                user_payload: (_c = (usePayloadKey ? JSON.parse(getToken(usePayloadKey)) : null)) !== null && _c !== void 0 ? _c : {
                    status: 'success',
                    message: 'OK',
                    data: {
                        status: 'success',
                        country: 'India',
                        countryCode: 'IN',
                        region: 'MH',
                        regionName: 'Maharashtra',
                        city: 'Mumbai',
                        zip: '400001',
                        lat: 19.076,
                        lon: 72.8777,
                        timezone: 'Asia/Kolkata',
                        isp: 'Amazon Technologies Inc',
                        org: 'AWS EC2 (ap-south-1)',
                        as: 'AS16509 Amazon.com, Inc.',
                        query: '3.7.72.245',
                    },
                    error_info: null,
                    language: 'en-US',
                    customerUniqueId: 'eb4504d06938b60a',
                    user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/136.0.0.0 Safari/537.36',
                },
                realtime_transcription: true,
                voice_type: voiceType,
            };
            socketRef.current.emit('start_audio_stream', payload);
            isRecordingRef.current = true;
            setStatus('Recording');
            mediaRecorderRef.current.ondataavailable = (event) => {
                var _a;
                if (!isRecordingRef.current)
                    return;
                if (event.data.size > 0 && ((_a = socketRef.current) === null || _a === void 0 ? void 0 : _a.connected)) {
                    event.data.arrayBuffer().then((buffer) => {
                        var _a;
                        (_a = socketRef.current) === null || _a === void 0 ? void 0 : _a.emit('audio_chunk', {
                            session_id: newSessionId,
                            chunk: buffer,
                            sequence: sequenceRef.current++,
                        });
                    });
                }
            };
            mediaRecorderRef.current.start(100);
        }
        catch (error) {
            console.error('Error starting recording:', error);
        }
    });
    const stopRecording = () => {
        var _a;
        if (!isRecordingRef.current)
            return;
        isRecordingRef.current = false;
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
        }
        if (streamRef.current)
            streamRef.current.getTracks().forEach((t) => t.stop());
        setIsLoading(true); // Start loader
        (_a = socketRef.current) === null || _a === void 0 ? void 0 : _a.emit('stop_audio_stream', { session_id: sessionId });
        console.time('started processing audio');
        setStatus('Stopped');
    };
    const toggleRecording = () => __awaiter(void 0, void 0, void 0, function* () {
        const startRecordingCallback = () => __awaiter(void 0, void 0, void 0, function* () {
            yield startRecording();
            setIsMicOff(false);
        });
        if (isMicOff) {
            // Start recording
            if (status === 'Disconnected') {
                connectToServer(startRecordingCallback);
            }
            else {
                yield startRecordingCallback();
            }
        }
        else {
            // Stop recording
            stopRecording();
            setIsMicOff(true);
        }
    });
    useEffect(() => {
        requestMicPermission();
        // Cleanup on unmount
        return () => {
            if (isRecordingRef.current) {
                stopRecording();
            }
            if (socketRef.current) {
                socketRef.current.disconnect();
            }
        };
    }, []);
    return (_jsx("div", { className: 'fixed inset-0 z-100002 flex items-center justify-center backdrop-blur-sm bg-black/30', children: _jsxs("div", { className: 'relative w-full h-full flex flex-col items-center justify-center', children: [_jsx(Button, { size: 'sm', variant: 'secondary', onClick: onClose, className: 'absolute top-4 right-4 cursor-pointer py-2 bg-white rounded-lg', children: _jsx(CloseIcon, {}) }), _jsxs("div", { className: 'relative w-[150px] h-[150px]', children: [!isMicOff && (_jsxs(_Fragment, { children: [_jsx("span", { className: 'absolute inset-0 rounded-full border-4 border-blue-400 animate-ripple z-10000' }), _jsx("span", { className: 'absolute inset-0 rounded-full border-4 border-blue-300 animate-ripple delay-300 z-10000' }), _jsx("span", { className: 'absolute inset-0 rounded-full border-4 border-blue-200 animate-ripple delay-600 z-10000' })] })), _jsx("div", { className: 'relative w-full h-full rounded-full overflow-hidden z-100002 border-4 border-blue-300 bg-[linear-gradient(to_right,#02C0FB,#FFFFFF)] shadow-[0px_4px_10px_0px_#000000]', children: _jsx("img", { src: 'https://dev.vov.systems.marktine.com/voice-interaction-avatar.jpg', alt: 'Voice', className: 'object-cover w-full h-full' }) })] }), _jsx(Button, { size: 'sm', variant: 'secondary', className: cn('mt-10 w-[50px] h-[50px] rounded-tr-full rounded-br-full rounded-bl-full rotate-45 flex items-center justify-center', isLoading && 'opacity-50 cursor-not-allowed', !isLoading && 'cursor-pointer bg-white shadow-md'), onClick: toggleRecording, disabled: isLoading, children: _jsx("div", { className: '-rotate-45', children: isMicOff ? _jsx(MicOffIcon, {}) : _jsx(MicIcon, {}) }) }), !isMicOff && (_jsx("div", { className: 'mt-6 text-2xl flex items-center justify-center text-white', children: "Listening..." })), isLoading && (_jsxs("div", { className: 'mt-6 flex items-center justify-center text-white', children: [_jsx("div", { className: 'w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin' }), _jsx("span", { className: 'ml-2 text-2xl', children: "Analyzing your speech " }), _jsxs("div", { className: 'flex gap-2 ', children: [_jsx("span", { className: 'text-2xl animate-bounce  transition delay-0', children: "." }), _jsx("span", { className: 'text-2xl animate-bounce transition  delay-200', children: "." }), _jsx("span", { className: 'text-2xl animate-bounce transition  delay-400', children: "." })] })] })), audioUrl && !isAudioPlaying && (_jsx("div", { className: 'mt-4 w-full flex justify-center', children: _jsx("audio", { controls: true, src: audioUrl, className: 'w-full max-w-[300px]' }) }))] }) }));
};
export default Modal;
